import type { ComponentProps, ReactNode } from 'react';
import { FormField, InputGroup, Paragraph, TextareaInput } from '@george-labs.com/design-system';

import { useField } from '@luna/form2';

import type { FormFieldProps } from './types/FormFieldProps';
import { shouldShowError } from './FieldErrorVisibilityService';

interface Props<FormValues> extends FormFieldProps<FormValues> {
    autoFocus?: boolean;
    prepend?: ReactNode;
    append?: ReactNode;
    rows?: number;
    autoGrow?: boolean;
    maxHeight?: number;
    maxLength?: number;
    inputProps?: Partial<ComponentProps<typeof TextareaInput>>;
    isErrorVisible?: boolean;
    shouldClearOnUnmount?: boolean;
}

const LABEL_COLUMN_WIDTH = 4;

export function FormTextareaField<FormValues = void>({
    label,
    placeholder,
    'data-cy': dataCy,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    labelPosition,
    fieldName,
    required,
    disabled,
    warning,
    info,
    alert,
    autoFocus,
    append,
    prepend,
    description,
    rows,
    autoGrow,
    maxHeight,
    maxLength,
    inputProps,
    isErrorVisible = true,
    showErrorBeforeBlur,
    addon,
    shouldClearOnUnmount,
}: Props<FormValues>) {
    const [{ value, onBlur, onChange, name }, { error, touched, dirty }] = useField(fieldName, {
        shouldClearOnUnmount,
    });

    const isFieldErrorVisible = shouldShowError(
        isErrorVisible,
        touched,
        error,
        showErrorBeforeBlur,
        dirty,
    );

    return (
        <FormField
            description={description}
            required={required}
            input={
                <InputGroup disabled={disabled} append={append} prepend={prepend}>
                    <TextareaInput
                        {...inputProps}
                        autoFocus={autoFocus}
                        data-cy={dataCy}
                        rows={rows}
                        autoGrow={autoGrow}
                        placeholder={placeholder || ''}
                        maxHeight={maxHeight}
                        maxLength={maxLength}
                        value={value}
                        onBlur={onBlur}
                        onChange={onChange}
                        name={name}
                    />
                </InputGroup>
            }
            label={label}
            placeholder={placeholder}
            error={
                isFieldErrorVisible && <Paragraph data-cy={`${dataCy}-error`}>{error}</Paragraph>
            }
            labelColumnWidth={labelColumnWidth}
            labelPosition={labelPosition}
            data-cy={dataCy}
            disabled={disabled}
            warning={warning}
            info={info}
            alert={alert}
            addon={addon}
        />
    );
}

FormTextareaField.LABEL_POSITION = FormField.LABEL_POSITION;
